/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: lookup_construction_purposes entity configuration
 * Generator: scripts/generate-api-clients.ts
 *
 * This file contains the API client for the lookup_construction_purposes table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the lookup_construction_purposes API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, code, name, description, sort_order, created_at, archived, updated_at]
 * Searchable fields: []
 * Filterable fields: [id]
 */

import { httpClient } from '../http-client.js';
import { type UnifiedQueryOptions } from '@config/search/global-search-config';
import type {
  LookupConstructionPurposesRow,
  LookupConstructionPurposesInsert,
  LookupConstructionPurposesUpdate
} from '../../types/generated_schemas/generated-lookup_construction_purposes-schema.js';

export interface LookupConstructionPurposesQueryOptions extends UnifiedQueryOptions {}

export interface LookupConstructionPurposesResponse {
  data: LookupConstructionPurposesRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for lookup_construction_purposes table operations
 *
 * @description Provides type-safe access to lookup_construction_purposes API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class LookupConstructionPurposesClient {
  private readonly basePath = '/lookup-construction-purposes';

  /**
   * Get all lookup_construction_purposes records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of lookup_construction_purposes records
   */
  async getAll(options?: LookupConstructionPurposesQueryOptions): Promise<LookupConstructionPurposesRow[]> {
    const params = new URLSearchParams();
    
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sort', options.sortBy);
    if (options?.sortOrder) params.set('order', options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    const response = await httpClient.get<LookupConstructionPurposesResponse>(url);
    return response.data;
  }

  /**
   * Get a single lookup_construction_purposes record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the lookup_construction_purposes record
   */
  async getById(id: string): Promise<LookupConstructionPurposesRow> {
    const response = await httpClient.get<{
      data: LookupConstructionPurposesRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new lookup_construction_purposes record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created lookup_construction_purposes record
   */
  async create(data: LookupConstructionPurposesInsert): Promise<LookupConstructionPurposesRow> {
    const response = await httpClient.post<{
      data: LookupConstructionPurposesRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing lookup_construction_purposes record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated lookup_construction_purposes record
   */
  async update(id: string, data: LookupConstructionPurposesUpdate): Promise<LookupConstructionPurposesRow> {
    const response = await httpClient.put<{
      data: LookupConstructionPurposesRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a lookup_construction_purposes record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple lookup_construction_purposes records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created lookup_construction_purposes records
   */
  async createMany(data: LookupConstructionPurposesInsert[]): Promise<LookupConstructionPurposesRow[]> {
    const response = await httpClient.post<{
      data: LookupConstructionPurposesRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Search lookup_construction_purposes with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    _query: string,
    _columns?: string[],
    _options?: LookupConstructionPurposesQueryOptions
  ): Promise<LookupConstructionPurposesResponse> {
    throw new Error('Search operations are not available for lookup_construction_purposes - no searchable fields configured');
  }

  /**
   * Get count of lookup_construction_purposes records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}/count?${queryString}` : `${this.basePath}/count`;
    
    const response = await httpClient.get<{ data: number; success: boolean }>(url);
    return response.data;
  }

  /**
   * Check if a lookup_construction_purposes record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: 'lookup_construction_purposes',
      publicFields: ['id', 'code', 'name', 'description', 'sort_order', 'created_at', 'archived', 'updated_at'],
      searchableFields: [],
      filterableFields: ['id'],
      sortableFields: ['created_at', 'updated_at'],
      readonlyFields: ['id', 'created_at', 'updated_at'],
      requiredFields: ['id', 'code', 'name', 'sort_order', 'created_at', 'archived', 'updated_at'],
    };
  }
}

// Export singleton instance for convenience
export const lookupConstructionPurposesClient = new LookupConstructionPurposesClient();
